import React, { useState, useRef, useEffect } from 'react'
import EmailVerificationEnvelopeIllustration from './EmailVerificationEnvelopeIllustration'

const OTP_LENGTH = 6
const RESEND_COOLDOWN_SEC = 30

// resendAvailableAt is when the backend's per-record cooldown (off lastSentAt)
// actually clears; seed/refresh the local countdown from it so "Resend" isn't
// clickable immediately after a send just because this is a fresh mount.
function cooldownSecondsFromTimestamp(resendAvailableAt) {
  if (!resendAvailableAt) return 0
  const seconds = Math.ceil((new Date(resendAvailableAt).getTime() - Date.now()) / 1000)
  return seconds > 0 ? seconds : 0
}

// Generic 6-digit code entry screen, shared by the verify-primary,
// update-primary and add-backup steps of the email verification flow.
const EmailVerificationCodeEntry = (props) => {
  const {
    email, title, description, resendAvailableAt, verifiedMessage, showIllustration,
    onSubmitCode, onResend, onBack, backLabel,
  } = props
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''))
  const [submitLoading, setSubmitLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [resendLoading, setResendLoading] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(() => cooldownSecondsFromTimestamp(resendAvailableAt))
  const inputRefs = useRef([])

  useEffect(() => {
    if (resendCooldown <= 0) return undefined
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendCooldown])

  function handleChange(index, value) {
    const char = value.replace(/\D/g, '').slice(-1)
    const newDigits = [...digits]
    newDigits[index] = char
    setDigits(newDigits)
    if (char && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1].focus()
    } else if (char && index === OTP_LENGTH - 1 && newDigits.every((d) => d)) {
      handleSubmitDigits(newDigits)
    }
  }

  function handleKeyDown(index, e) {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1].focus()
    }
  }

  function handlePaste(e) {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
    const newDigits = [...digits]
    pasted.split('').forEach((char, i) => { newDigits[i] = char })
    setDigits(newDigits)
    const nextEmpty = newDigits.findIndex((d) => !d)
    const focusIndex = nextEmpty === -1 ? OTP_LENGTH - 1 : nextEmpty
    inputRefs.current[focusIndex].focus()
    if (nextEmpty === -1) {
      handleSubmitDigits(newDigits)
    }
  }

  async function handleSubmitDigits(currentDigits) {
    const code = currentDigits.join('')
    if (code.length < OTP_LENGTH) return
    try {
      setSubmitLoading(true)
      setErrorMessage('')
      await onSubmitCode(code)
    } catch (err) {
      setSubmitLoading(false)
      setErrorMessage(err.message || 'Incorrect code. Please try again.')
      setDigits(Array(OTP_LENGTH).fill(''))
      if (inputRefs.current[0]) inputRefs.current[0].focus()
    }
  }

  function handleSubmit(e) {
    e.preventDefault()
    handleSubmitDigits(digits)
  }

  async function handleResend() {
    try {
      setResendLoading(true)
      setResendCooldown(0)
      setErrorMessage('')
      const response = await onResend()
      setResendCooldown(cooldownSecondsFromTimestamp(response && response.resendAvailableAt) || RESEND_COOLDOWN_SEC)
    } catch (err) {
      setErrorMessage(err.message || 'Failed to resend code. Please try again.')
    } finally {
      setResendLoading(false)
    }
  }

  return (
    <div className='emailVerificationFlow-codeEntry'>
      {showIllustration && <EmailVerificationEnvelopeIllustration />}
      <div className='emailVerificationFlow-title'>{title}</div>
      <div className='emailVerificationFlow-emailDisplay'>{email}</div>
      <div className='emailVerificationFlow-description'>{description}</div>
      <form className='emailVerificationFlow-codeEntry-main' onSubmit={handleSubmit}>
        <div className='emailVerificationFlow-codeEntry-digitRow'>
          {digits.map((digit, index) => (
            <input
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              ref={(el) => { inputRefs.current[index] = el }}
              className='emailVerificationFlow-codeEntry-digitInput'
              type='tel'
              inputMode='numeric'
              autoComplete='off'
              maxLength={1}
              value={digit}
              data-filled={digit ? 'true' : 'false'}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onFocus={(e) => e.target.select()}
              onPaste={handlePaste}
              autoFocus={index === 0}
            />
          ))}
        </div>
        {errorMessage && (
          <div className='emailVerificationFlow-error'>{errorMessage}</div>
        )}
        {!verifiedMessage && (
          <div className='emailVerificationFlow-codeEntry-resend'>
            {resendCooldown > 0 ? (
              <span className='emailVerificationFlow-codeEntry-resend-success'>
                {`Sent! You can resend in ${resendCooldown}s`}
              </span>
            ) : (
              <button
                type='button'
                className='emailVerificationFlow-codeEntry-resend-btn'
                onClick={handleResend}
                disabled={resendLoading}
              >
                {resendLoading ? 'Sending...' : 'Resend'}
              </button>
            )}
            {onBack && (
              <React.Fragment>
                <span className='emailVerificationFlow-codeEntry-resend-separator'>·</span>
                <button
                  type='button'
                  className='emailVerificationFlow-codeEntry-resend-btn'
                  onClick={onBack}
                >
                  {backLabel || 'Back'}
                </button>
              </React.Fragment>
            )}
          </div>
        )}
        {verifiedMessage && (
          <div className='emailVerificationFlow-codeEntry-verifiedMessage'>{verifiedMessage}</div>
        )}
        <button
          type='submit'
          className={`btn btn--blue btn--large${(submitLoading || verifiedMessage) ? ' btn--disabled' : ''}`}
          disabled={submitLoading || !!verifiedMessage}
        >
          Continue
        </button>
      </form>
    </div>
  )
}

EmailVerificationCodeEntry.defaultProps = {
  showIllustration: true,
}

export default EmailVerificationCodeEntry
